import { useState, useEffect, useCallback, createContext, useContext } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Volume2, VolumeX, Play, Clock, Crown, ExternalLink, Loader2, Sparkles } from 'lucide-react';
import { Button } from './ui/button';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ==================
// AD CONTEXT - Global ad state management
// ==================
const AdContext = createContext(null);

export function AdProvider({ children }) {
  const { user, isPremium } = useAuth();
  const [adConfig, setAdConfig] = useState(null);
  const [adsenseLoaded, setAdsenseLoaded] = useState(false);

  // Fetch ad configuration
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const response = await fetch(`${API_URL}/api/ads/config`);
        if (response.ok) {
          const config = await response.json();
          console.log('[AdSystem] Config loaded:', config.enabled ? 'AdSense enabled' : 'Placeholder mode');
          setAdConfig(config);
          
          // Load AdSense script if configured
          if (config?.enabled && config?.publisher_id) {
            loadAdSenseScript(config.publisher_id);
          }
        }
      } catch (err) {
        console.log('Ad config fetch error:', err);
      }
    };
    fetchConfig();
  }, []);

  const loadAdSenseScript = (publisherId) => {
    if (document.querySelector('script[src*="pagead2.googlesyndication.com"]')) {
      setAdsenseLoaded(true);
      return;
    }
    
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${publisherId}`;
    script.crossOrigin = 'anonymous';
    script.onload = () => setAdsenseLoaded(true);
    document.head.appendChild(script);
  };

  const shouldShowAds = !isPremium;

  return (
    <AdContext.Provider value={{ adConfig, shouldShowAds, adsenseLoaded, isPremium }}>
      {children}
    </AdContext.Provider>
  );
}

export function useAds() {
  const context = useContext(AdContext);
  if (!context) {
    // Return safe defaults if not in AdProvider
    return { 
      adConfig: null, 
      shouldShowAds: false, 
      adsenseLoaded: false,
      isPremium: true 
    };
  }
  return context;
}

// ==================
// VIDEO AD MODAL - Pre-roll before major actions
// ==================
export function VideoAdModal({ 
  isOpen, 
  onComplete, 
  placement = 'analysis',
  actionLabel = 'votre analyse'
}) {
  const { getAuthHeaders, isPremium } = useAuth();
  const [timeLeft, setTimeLeft] = useState(10);
  const [canSkip, setCanSkip] = useState(false);
  const [muted, setMuted] = useState(true);
  const [impressionId, setImpressionId] = useState(null);
  const [videoProgress, setVideoProgress] = useState(0);

  // Premium users skip ads entirely
  useEffect(() => {
    if (isOpen && isPremium) {
      onComplete();
    }
  }, [isOpen, isPremium, onComplete]);

  // Track impression
  useEffect(() => {
    if (isOpen && !isPremium && !impressionId) {
      trackImpression('video_preroll', placement);
    }
  }, [isOpen, isPremium]);

  const trackImpression = async (adType, adPlacement) => {
    try {
      const response = await fetch(`${API_URL}/api/ads/impression?ad_type=${adType}&ad_placement=${adPlacement}`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include'
      });
      const data = await response.json();
      setImpressionId(data.impression_id);
    } catch (err) {
      console.log('Ad tracking error:', err);
    }
  };

  const markComplete = async () => {
    if (impressionId) {
      try {
        await fetch(`${API_URL}/api/ads/complete/${impressionId}`, {
          method: 'POST',
          headers: getAuthHeaders(),
          credentials: 'include'
        });
      } catch (err) {
        console.log('Ad complete error:', err);
      }
    }
    onComplete();
  };

  // Countdown timer & progress
  useEffect(() => {
    if (!isOpen || isPremium || timeLeft <= 0) return;

    const timer = setInterval(() => {
      setTimeLeft(prev => {
        const newTime = prev - 1;
        setVideoProgress(((10 - newTime) / 10) * 100);
        if (newTime <= 5) {
          setCanSkip(true);
        }
        if (newTime <= 0) {
          clearInterval(timer);
          return 0;
        }
        return newTime;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [isOpen, isPremium, timeLeft]);

  // Reset when modal opens
  useEffect(() => {
    if (isOpen && !isPremium) {
      setTimeLeft(10);
      setCanSkip(false);
      setImpressionId(null);
      setVideoProgress(0);
    }
  }, [isOpen, isPremium]);

  // Auto-complete when timer ends
  useEffect(() => {
    if (timeLeft === 0 && isOpen) {
      markComplete();
    }
  }, [timeLeft, isOpen]);

  if (!isOpen || isPremium) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-sm flex items-center justify-center p-4"
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="bg-zinc-900 border border-zinc-800 rounded-2xl w-full max-w-lg overflow-hidden"
        >
          {/* Header */}
          <div className="p-4 border-b border-zinc-800 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gold-500/20 rounded-full flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-gold-500" />
              </div>
              <div>
                <h3 className="text-white font-semibold">Préparation de {actionLabel}</h3>
                <p className="text-zinc-500 text-sm">Publicité - {timeLeft}s restantes</p>
              </div>
            </div>
            <button
              onClick={() => setMuted(!muted)}
              className="p-2 text-zinc-500 hover:text-white transition-colors"
            >
              {muted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
            </button>
          </div>

          {/* Video Ad Placeholder */}
          <div className="aspect-video bg-gradient-to-br from-zinc-800 to-zinc-900 relative overflow-hidden">
            {/* Progress bar */}
            <div className="absolute top-0 left-0 right-0 h-1 bg-zinc-800">
              <motion.div 
                className="h-full bg-gold-500"
                initial={{ width: 0 }}
                animate={{ width: `${videoProgress}%` }}
                transition={{ duration: 0.3 }}
              />
            </div>

            {/* Ad content placeholder - replace with real AdSense/video */}
            <div className="absolute inset-0 flex flex-col items-center justify-center p-6">
              <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center mb-4 shadow-lg shadow-blue-500/25">
                <Play className="w-10 h-10 text-white" fill="white" />
              </div>
              
              <div className="text-center">
                <p className="text-white font-bold text-lg mb-1">Espace publicitaire</p>
                <p className="text-zinc-400 text-sm mb-4">Partenaire officiel</p>
                
                {/* Fake ad content */}
                <div className="bg-zinc-800/50 rounded-xl p-4 border border-zinc-700">
                  <p className="text-emerald-400 font-semibold mb-1">Rénovez malin !</p>
                  <p className="text-zinc-300 text-sm">Comparez les artisans de votre région</p>
                </div>
              </div>

              {/* Timer overlay */}
              <div className="absolute top-4 right-4 bg-black/50 rounded-full px-3 py-1 flex items-center gap-2">
                <Clock className="w-4 h-4 text-gold-500" />
                <span className="text-white font-mono text-sm">{timeLeft}s</span>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="p-4 border-t border-zinc-800">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-zinc-500 text-sm">
                <span className="px-2 py-0.5 bg-zinc-800 rounded text-xs uppercase">Pub</span>
                <span>Votre contenu arrive bientôt</span>
              </div>
              
              {canSkip ? (
                <Button
                  onClick={markComplete}
                  className="bg-gold-500 hover:bg-gold-400 text-black font-semibold"
                  data-testid="skip-ad-btn"
                >
                  Passer la pub
                </Button>
              ) : (
                <div className="flex items-center gap-2 text-zinc-500">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span className="text-sm">Passer dans {Math.max(0, timeLeft - 5)}s</span>
                </div>
              )}
            </div>

            {/* Premium upsell */}
            <div className="mt-3 pt-3 border-t border-zinc-800">
              <button 
                onClick={() => window.location.href = '/subscription'}
                className="w-full flex items-center justify-center gap-2 text-gold-500 hover:text-gold-400 transition-colors text-sm"
              >
                <Crown className="w-4 h-4" />
                <span>Passer Premium pour supprimer les pubs</span>
              </button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

// ==================
// BANNER AD COMPONENT - Sticky bottom or inline
// ==================
export function BannerAd({ 
  placement = 'bottom', 
  className = '',
  sticky = false 
}) {
  const { getAuthHeaders, isPremium } = useAuth();
  const { adConfig, adsenseLoaded } = useAds();
  const [dismissed, setDismissed] = useState(false);
  const [impressionId, setImpressionId] = useState(null);

  // Track impression
  useEffect(() => {
    if (!isPremium && !dismissed && !impressionId) {
      trackImpression();
    }
  }, [isPremium, dismissed]);

  const trackImpression = async () => {
    try {
      const response = await fetch(`${API_URL}/api/ads/impression?ad_type=banner&ad_placement=${placement}`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include'
      });
      const data = await response.json();
      setImpressionId(data.impression_id);
    } catch (err) {
      console.log('Banner tracking error:', err);
    }
  };

  const handleClick = async () => {
    if (impressionId) {
      try {
        await fetch(`${API_URL}/api/ads/click/${impressionId}`, {
          method: 'POST',
          headers: getAuthHeaders(),
          credentials: 'include'
        });
      } catch (err) {
        console.log('Click tracking error:', err);
      }
    }
  };

  // Don't show for premium users
  if (isPremium || dismissed) return null;

  // Check if AdSense is properly configured
  const adsenseEnabled = adConfig?.enabled && adConfig?.publisher_id;

  const bannerContent = (
    <div 
      className={`relative bg-gradient-to-r from-zinc-900 to-zinc-800 border border-zinc-700/50 rounded-xl overflow-hidden ${className}`}
      onClick={handleClick}
    >
      {/* AdSense or Placeholder */}
      <div className="min-h-[70px] flex items-center justify-between p-3 gap-4">
        {adsenseEnabled && adsenseLoaded ? (
          /* Real AdSense Banner */
          <ins 
            className="adsbygoogle flex-1"
            style={{ display: 'block', minHeight: '60px' }}
            data-ad-client={adConfig.publisher_id}
            data-ad-slot={adConfig.slots?.[`banner_${placement}`] || ''}
            data-ad-format="horizontal"
            data-full-width-responsive="true"
          />
        ) : (
          /* Placeholder Banner */
          <>
            <div className="flex items-center gap-3 flex-1">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-500 rounded-lg flex items-center justify-center flex-shrink-0">
                <ExternalLink className="w-6 h-6 text-white" />
              </div>
              <div className="min-w-0">
                <p className="text-white font-medium text-sm truncate">Espace publicitaire partenaire</p>
                <p className="text-zinc-500 text-xs">Cliquez pour en savoir plus</p>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="border-gold-500/50 text-gold-500 hover:bg-gold-500/10 text-xs flex-shrink-0"
              onClick={(e) => {
                e.stopPropagation();
                window.location.href = '/subscription';
              }}
            >
              <Crown className="w-3 h-3 mr-1" />
              Sans pub
            </Button>
          </>
        )}
      </div>

      {/* Close button */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          setDismissed(true);
        }}
        className="absolute top-1 right-1 p-1 text-zinc-600 hover:text-zinc-400 transition-colors"
      >
        <X className="w-4 h-4" />
      </button>

      {/* Ad label */}
      <span className="absolute top-1 left-2 px-1.5 py-0.5 bg-zinc-700/50 rounded text-[9px] text-zinc-500 uppercase tracking-wider">
        Pub
      </span>
    </div>
  );

  // Sticky positioning for bottom banner
  if (sticky && placement === 'bottom') {
    return (
      <div className="fixed bottom-0 left-0 right-0 z-40 p-3 bg-gradient-to-t from-black/80 to-transparent pointer-events-none">
        <div className="max-w-4xl mx-auto pointer-events-auto">
          {bannerContent}
        </div>
      </div>
    );
  }

  return bannerContent;
}

// ==================
// AD GATE HOOK - Wrap actions that require watching an ad
// ==================
export function useAdGate(actionType = 'analysis') {
  const { isPremium } = useAuth();
  const [showAd, setShowAd] = useState(false);
  const [pendingCallback, setPendingCallback] = useState(null);

  const actionLabels = {
    'analysis': 'votre analyse',
    'diagnostic': 'votre diagnostic',
    'recipe': 'votre recette',
    'pdf': 'votre export PDF'
  };

  const requireAd = useCallback((callback) => {
    if (isPremium) {
      // Premium users skip ads
      callback();
      return;
    }
    
    // Free users must watch ad
    setPendingCallback(() => callback);
    setShowAd(true);
  }, [isPremium]);

  const onAdComplete = useCallback(() => {
    setShowAd(false);
    if (pendingCallback) {
      pendingCallback();
      setPendingCallback(null);
    }
  }, [pendingCallback]);

  const AdGateModal = useCallback(() => (
    <VideoAdModal
      isOpen={showAd}
      onComplete={onAdComplete}
      placement={actionType}
      actionLabel={actionLabels[actionType] || 'votre contenu'}
    />
  ), [showAd, onAdComplete, actionType]);

  return {
    requireAd,
    AdGateModal,
    isAdShowing: showAd
  };
}

// ==================
// INLINE RESULT AD - Shows between results
// ==================
export function InlineResultAd({ className = '' }) {
  const { isPremium } = useAuth();
  
  if (isPremium) return null;

  return (
    <div className={`my-6 ${className}`}>
      <BannerAd placement="inline" />
    </div>
  );
}

// ==================
// PDF AD BANNER - For PDF generation (legacy function)
// ==================
export function getPdfAdBanner(isPremium) {
  if (isPremium) return null;
  
  return {
    text: "Généré par Assistant by Léon - assistantbyleon.fr | Passez Premium pour supprimer cette mention",
    position: "bottom",
    style: {
      backgroundColor: "#f5f5f5",
      padding: "10px",
      fontSize: "10px",
      color: "#666",
      textAlign: "center",
      borderTop: "1px solid #ddd"
    }
  };
}

// ==================
// PDF AD BANNER COMPONENT - Visual banner for printable PDFs
// ==================
export function PdfAdBanner({ className = '' }) {
  const { isPremium } = useAuth();
  
  // Premium users don't see ads
  if (isPremium) return null;

  return (
    <div className={`mt-8 pt-6 border-t-2 border-amber-400 print:mt-4 print:pt-4 ${className}`}>
      <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-lg p-4 print:p-3">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-500 rounded-lg flex items-center justify-center flex-shrink-0 print:w-8 print:h-8">
              <Crown className="w-5 h-5 text-white print:w-4 print:h-4" />
            </div>
            <div>
              <p className="text-amber-800 font-semibold text-sm">Document généré avec la version Gratuite</p>
              <p className="text-amber-600 text-xs">Passez Premium pour supprimer cette mention et accéder à toutes les fonctionnalités</p>
            </div>
          </div>
          <div className="text-right flex-shrink-0">
            <p className="text-amber-700 font-bold text-lg">9,99€<span className="text-xs font-normal text-amber-600">/mois</span></p>
            <p className="text-amber-600 text-xs">Sans engagement</p>
          </div>
        </div>
        <div className="mt-3 pt-3 border-t border-amber-200 flex items-center justify-center gap-2 text-amber-700 text-xs">
          <ExternalLink className="w-3 h-3" />
          <span>assistantbyleon.fr/subscription</span>
        </div>
      </div>
      <p className="text-center text-xs text-zinc-400 mt-2 print:mt-1">
        Publicité - Assistant by Léon, un produit de l'écosystème Phénix
      </p>
    </div>
  );
}

export default { 
  VideoAdModal, 
  BannerAd, 
  useAdGate, 
  InlineResultAd,
  AdProvider,
  useAds,
  getPdfAdBanner,
  PdfAdBanner
};
